/**
 * useSmartChoiceContext
 *
 * Gathers SmartChoice context from all data sources (URL, stores, etc.)
 * Separated for single responsibility and easy testing
 */

import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { useIsNiceMember } from '../../useIsNiceMember';
import { SmartChoiceContext } from '../smartChoice.config';

/**
 * Hook to gather complete SmartChoice context from various sources
 *
 * @param coverageType - Optional coverage type override (for widget mode)
 * @returns Memoized context object
 */
export function useSmartChoiceContext(
  coverageType?: string
): SmartChoiceContext {
  // Gather from stores
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue: chipValueFromStore } = chipStore;

  const { providerType = '' } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      providerType: state.typeaheadSearchStore.providerType,
    }))
  );

  const isNiceMember = useIsNiceMember();

  // Parse URL parameters on every render so the memo below stays in sync with
  // the current URL (window.location.search is not reactive on its own, and on
  // the map view page the coverage type is not synced into the chip store).
  const searchParams = new URLSearchParams(window.location.search);
  const chipValueFromParams = searchParams.get('chipValue');
  const coverageTypeFromParams = searchParams.get('coverageType');
  const sectionTypeFromParams = searchParams.get('sectionType') || undefined;
  const searchTypeFromParams = searchParams.get('searchType') || undefined;
  const searchBySpecialityFromParams =
    searchParams.get('searchBySpeciality') || undefined;

  // Build context from all sources (memoized)
  return useMemo(
    () => ({
      // Category - URL takes precedence over store
      chipValue: chipValueFromParams || chipValueFromStore,
      category: chipValueFromParams || chipValueFromStore,

      // Search context from URL
      sectionType: sectionTypeFromParams,
      searchType: searchTypeFromParams,
      searchBySpeciality: searchBySpecialityFromParams,

      // Provider context from store
      providerType,

      // Member context
      isNiceMember,

      // Coverage context (for widget mode)
      // Priority: explicit param > URL param > store value
      coverageType:
        coverageType ?? coverageTypeFromParams ?? chipStore.coverageType,
    }),
    [
      chipValueFromParams,
      chipValueFromStore,
      sectionTypeFromParams,
      searchTypeFromParams,
      searchBySpecialityFromParams,
      coverageTypeFromParams,
      providerType,
      isNiceMember,
      coverageType,
      chipStore.coverageType,
    ]
  );
}
